import { MsalGuardConfiguration, MsalInterceptorConfiguration } from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MICROSOFT_SSO_CONFIG,
  getRedirectUri,
} from './sso.config';

/**
 * MSAL Configuration for Azure AD Integration
 *
 * This file configures Microsoft Authentication Library (MSAL) for the Angular application.
 * It creates the MSAL instance, guard, and interceptor configurations needed for Azure AD SSO.
 *
 * Key Components:
 * - MSALInstanceFactory: Creates the main MSAL client instance
 * - MSALInterceptorConfigFactory: Configures automatic token injection for API calls
 * - MSALGuardConfigFactory: Configures route protection behavior
 *
 * IMPORTANT: Ensure that MICROSOFT_SSO_CONFIG in sso.config.ts has been properly
 * configured with your Azure AD app registration details before using these factories.
 */

/**
 * MSAL Logger Callback
 *
 * Handles logging output from MSAL. Useful for debugging authentication issues.
 * Uncomment the console.log line below to enable detailed MSAL logging during development.
 *
 * @param logLevel - The severity level of the log message
 * @param message - The log message content
 */
export function loggerCallback(_logLevel: LogLevel, _message: string) {
  // Uncomment the line below for debugging MSAL authentication issues:
  // console.log(`MSAL [${LogLevel[logLevel]}]:`, message);
}

/**
 * MSAL Instance Factory
 *
 * Creates and configures the main MSAL PublicClientApplication instance.
 * This instance handles all authentication operations including login, logout,
 * token acquisition, and account management.
 *
 * Configuration Details:
 * - clientId: Your Azure AD app registration client ID
 * - authority: Your Azure AD tenant authority URL
 * - redirectUri: Where users are redirected after authentication
 * - cacheLocation: Where authentication tokens are stored (localStorage vs sessionStorage)
 * - storeAuthStateInCookie: Required for IE11 support
 *
 * @returns Configured MSAL PublicClientApplication instance
 */
export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: MICROSOFT_SSO_CONFIG.clientId, // Azure AD App Registration Client ID
      authority: MICROSOFT_SSO_CONFIG.authority, // Azure AD Authority URL with tenant ID
      redirectUri: getRedirectUri(), // Redirect URI from environment config
      // clientCapabilities: ['CP1'],               // Optional: Enable Conditional Access
    },
    cache: {
      // SESSION storage (task 4.3, Defect 2): MSAL's cache holds the Microsoft
      // id_token AND refresh token. In localStorage they survive the tab and
      // are replayable against /parent-auth/sso/microsoft (the backend has no
      // nonce binding), so XSS could mint a gtlc_parent_session cookie and
      // bypass the httpOnly cookie model entirely. SessionStorage scopes the
      // cache to the tab lifetime; the GTLC cookie is the durable auth and
      // Microsoft's own SSO cookie re-authenticates silently on return.
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info, // Set to LogLevel.Verbose for detailed debugging
        piiLoggingEnabled: false, // Disable personally identifiable information logging
      },
    },
  });
}

/**
 * MSAL Interceptor Configuration Factory
 *
 * Configures HTTP request interception to automatically attach access tokens
 * to API calls. This eliminates the need to manually add authorization headers
 * to every HTTP request.
 *
 * How it works:
 * 1. Define protected resources (API endpoints) and their required scopes
 * 2. MSAL interceptor matches outgoing requests against protected resource map
 * 3. For matching requests, automatically acquires and attaches access tokens
 * 4. Handles token refresh automatically when tokens expire
 *
 * Configuration includes:
 * - Microsoft Graph API endpoints for user profile access
 * - Your backend API endpoints with custom scopes
 * - Environment-specific API endpoints
 *
 * @returns Configured MSAL interceptor configuration
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  // Microsoft Graph API - for accessing user profile information
  protectedResourceMap.set('https://graph.microsoft.com/*', MICROSOFT_SSO_CONFIG.scopes);

  // COOKIE-SESSION MODEL (task 4.1): the GTLC backend API is deliberately
  // NOT in the protected resource map. Requests to environment.apiUrl are
  // authenticated by the httpOnly gtlc_parent_session cookie (withCredentials
  // in api.service.ts), NOT by a bearer token — the MSAL interceptor must
  // never attach an Authorization header to them. See design.md Decision 1.

  return {
    interactionType: InteractionType.Redirect, // Use redirect flow for token acquisition
    protectedResourceMap, // Map of URLs to scopes for token attachment
  };
}

/**
 * MSAL Guard Configuration Factory
 *
 * Configures the MSAL Guard behavior for route protection.
 * The guard automatically redirects users to Azure AD login when they access
 * protected routes without being authenticated.
 *
 * Configuration options:
 * - interactionType: How authentication should be initiated (redirect vs popup)
 * - authRequest: Default scopes to request during authentication
 * - loginFailedRoute: Where to redirect users if authentication fails
 *
 * Note: In this app, we use a custom authGuard that redirects to our login page
 * instead of directly to Azure AD, so this configuration primarily serves as
 * a fallback for MSAL-specific route protection scenarios.
 *
 * @returns Configured MSAL guard configuration
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect, // Use redirect flow for authentication
    authRequest: {
      scopes: MICROSOFT_SSO_CONFIG.scopes, // Request these scopes during authentication
    },
    loginFailedRoute: '/login', // Redirect to login page on authentication failure
  };
}
